import React, { useEffect, useState } from "react";
import { Plus, Search, Edit, Trash2, Truck, Phone, Mail, MapPin, Star } from "lucide-react";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input, Select, Label, Textarea } from "../components/ui/Input";
import { Badge } from "../components/ui/Badge";
import { Modal } from "../components/ui/Modal";
import { toast } from "../components/ui/Toast";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import { formatVND, formatDate } from "../lib/utils";

const EMPTY = {
  name: "",
  code: "",
  phone: "",
  email: "",
  address: "",
  district: "",
  city: "",
  tax_id: "",
  group: "default",
  rating: 5,
  notes: "",
};

const GROUPS = [
  { value: "default", label: "Thông thường" },
  { value: "priority", label: "Ưu tiên" },
  { value: "inactive", label: "Ngừng hợp tác" },
];

const RATING_STARS = [1, 2, 3, 4, 5];

export default function Suppliers() {
  const { t } = useI18n();
  const { user } = useAuth();
  const canCreate = user?.role === "admin" || (user?.permissions_effective || user?.permissions || []).includes("suppliers.create");
  const canEdit   = user?.role === "admin" || (user?.permissions_effective || user?.permissions || []).includes("suppliers.edit");
  const canDelete = user?.role === "admin" || (user?.permissions_effective || user?.permissions || []).includes("suppliers.delete");

  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [group, setGroup] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const params = {};
    if (q) params.q = q;
    if (group) params.group = group;
    const { data } = await api.get("/suppliers", { params });
    setItems(data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [group]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };

  const openEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name || "",
      code: s.code || "",
      phone: s.phone || "",
      email: s.email || "",
      address: s.address || "",
      district: s.district || "",
      city: s.city || "",
      tax_id: s.tax_id || "",
      group: s.group || "default",
      rating: s.rating ?? 5,
      notes: s.notes || "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/suppliers/${editing.supplier_id}`, form);
      } else {
        await api.post("/suppliers", form);
      }
      toast({ title: t("common.saved"), variant: "success" });
      setOpen(false);
      load();
    } catch (err) {
      toast({ title: err?.response?.data?.detail || t("common.error"), variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`${t("common.delete")} nhà cung cấp "${s.name}"?`)) return;
    try {
      await api.delete(`/suppliers/${s.supplier_id}`);
      toast({ title: t("common.deleted"), variant: "success" });
      load();
    } catch (err) {
      toast({ title: err?.response?.data?.detail || t("common.error"), variant: "error" });
    }
  };

  const groupVariant = (g) => g === "priority" ? "delivered" : g === "inactive" ? "cancelled" : "regular";
  const groupLabel  = (g) => GROUPS.find((x) => x.value === g)?.label || g;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="suppliers-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-2">
            <Truck size={28} className="text-bamboo" /> Nhà cung cấp
          </h1>
          <p className="text-sm text-ink-secondary mt-1">{items.length} nhà cung cấp</p>
        </div>
        {canCreate && (
          <Button onClick={openCreate} data-testid="supplier-new-button">
            <Plus size={16} /> Thêm NCC
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2">
              <Label>{t("common.search")}</Label>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && load()}
                  placeholder="Tên hoặc số điện thoại..."
                  className="pl-8"
                  data-testid="supplier-search"
                />
              </div>
            </div>
            <div>
              <Label>Nhóm</Label>
              <Select value={group} onChange={(e) => setGroup(e.target.value)} data-testid="supplier-group-filter">
                <option value="">Tất cả</option>
                {GROUPS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* List */}
      {items.length === 0 ? (
        <div className="text-center py-16 text-ink-muted">
          <Truck size={40} className="mx-auto mb-3 opacity-30" />
          <p>Chưa có nhà cung cấp nào</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="suppliers-grid">
          {items.map((s) => (
            <Card key={s.supplier_id} className="hover:-translate-y-0.5 hover:shadow-md transition-all" data-testid={`supplier-card-${s.supplier_id}`}>
              <CardContent className="!p-4 space-y-3">
                {/* Top row: name + group badge */}
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-heading font-semibold text-ink truncate">{s.name}</div>
                    {s.code && <div className="text-xs text-ink-muted font-mono">{s.code}</div>}
                  </div>
                  <Badge variant={groupVariant(s.group)}>{groupLabel(s.group)}</Badge>
                </div>

                {/* Contact info */}
                <div className="space-y-1 text-sm">
                  {s.phone && (
                    <div className="flex items-center gap-2 text-ink-secondary">
                      <Phone size={13} className="shrink-0 text-ink-muted" />
                      <a href={`tel:${s.phone}`} className="hover:text-bamboo">{s.phone}</a>
                    </div>
                  )}
                  {s.email && (
                    <div className="flex items-center gap-2 text-ink-secondary">
                      <Mail size={13} className="shrink-0 text-ink-muted" />
                      <span className="truncate">{s.email}</span>
                    </div>
                  )}
                  {(s.address || s.city) && (
                    <div className="flex items-center gap-2 text-ink-secondary">
                      <MapPin size={13} className="shrink-0 text-ink-muted" />
                      <span className="truncate">{[s.address, s.district, s.city].filter(Boolean).join(", ")}</span>
                    </div>
                  )}
                </div>

                {/* Rating */}
                <div className="flex items-center gap-1">
                  {RATING_STARS.map((n) => (
                    <Star key={n} size={13} className={n <= (s.rating ?? 5) ? "text-amber-400 fill-amber-400" : "text-ink-muted"} />
                  ))}
                </div>

                {/* Debt + dates */}
                <div className="flex items-center justify-between text-xs text-ink-muted">
                  <span>
                    Công nợ:{" "}
                    <span className={`font-mono font-medium ${(s.total_debt || 0) > 0 ? "text-red-600" : "text-green-700"}`}>
                      {formatVND(s.total_debt || 0)}
                    </span>
                  </span>
                  {s.created_at && <span>Từ {formatDate(s.created_at)}</span>}
                </div>

                {s.notes && (
                  <div className="text-xs text-ink-muted italic border-t border-border pt-2 line-clamp-2">{s.notes}</div>
                )}

                {/* Actions */}
                <div className="flex justify-end gap-1 border-t border-border pt-2">
                  {canEdit && (
                    <Button variant="ghost" size="icon" onClick={() => openEdit(s)} data-testid={`supplier-edit-${s.supplier_id}`} title="Sửa">
                      <Edit size={14} />
                    </Button>
                  )}
                  {canDelete && (
                    <Button variant="ghost" size="icon" onClick={() => remove(s)} data-testid={`supplier-delete-${s.supplier_id}`} title="Xóa">
                      <Trash2 size={14} className="text-red-500" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modal thêm/sửa */}
      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Sửa nhà cung cấp" : "Thêm nhà cung cấp"} size="lg" testId="supplier-modal">
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Label>Tên nhà cung cấp *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                placeholder="VD: Công ty TNHH Bột Mì ABC"
                data-testid="supplier-form-name"
              />
            </div>
            <div>
              <Label>Mã NCC</Label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="Tự sinh nếu để trống" />
            </div>
            <div>
              <Label>Nhóm</Label>
              <Select value={form.group} onChange={(e) => setForm({ ...form, group: e.target.value })}>
                {GROUPS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
              </Select>
            </div>
            <div>
              <Label>Số điện thoại</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="0901234567" data-testid="supplier-form-phone" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="ncc@email.com" />
            </div>
            <div className="sm:col-span-2">
              <Label>Địa chỉ</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Số nhà, tên đường..." />
            </div>
            <div>
              <Label>Quận/Huyện</Label>
              <Input value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })} />
            </div>
            <div>
              <Label>Tỉnh/Thành phố</Label>
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="TP. Hồ Chí Minh" />
            </div>
            <div>
              <Label>Mã số thuế</Label>
              <Input value={form.tax_id} onChange={(e) => setForm({ ...form, tax_id: e.target.value })} />
            </div>
            <div>
              <Label>Đánh giá</Label>
              <div className="flex items-center gap-1 mt-1">
                {RATING_STARS.map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setForm({ ...form, rating: n })}
                    className="p-0.5 hover:scale-110 transition-transform"
                  >
                    <Star size={20} className={n <= form.rating ? "text-amber-400 fill-amber-400" : "text-ink-muted"} />
                  </button>
                ))}
                <span className="text-xs text-ink-muted ml-1">{form.rating}/5</span>
              </div>
            </div>
            <div className="sm:col-span-2">
              <Label>Ghi chú</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" type="button" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
            <Button type="submit" disabled={saving} data-testid="supplier-form-submit">
              {saving ? "Đang lưu..." : t("common.save")}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}